import React from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  Linkedin,
  Instagram,
  Facebook,
  Youtube,
  MessageCircle,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { BrandWordmark } from "@/components/Brand";

const socials = [
  { id: "in", Icon: Linkedin, href: "#" },
  { id: "ig", Icon: Instagram, href: "#" },
  { id: "fb", Icon: Facebook, href: "#" },
  { id: "yt", Icon: Youtube, href: "#" },
];

const Footer = ({ onRequestDemo }) => {
  const { t } = useLang();
  const solutionNames = t.solutions.items.map((s) => s.name);
  const channelNames = ["WhatsApp", "SMS", "RCS", "E-mail", "Voz"];

  const legalRoutes = {
    "Política de Privacidade": "/politica-de-privacidade",
    "Privacy Policy": "/politica-de-privacidade",
    "Termos de Uso": "/termos-de-uso",
    "Terms of Use": "/termos-de-uso",
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#04060d] border-t border-[#1dc7e9]/10 pt-16 pb-10"
    >
      <div
        aria-hidden
        className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[200px] glow-radial opacity-40 pointer-events-none"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Top: brand + 4 cols */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 mb-12">
          {/* Brand */}
          <div className="md:col-span-4 lg:col-span-5">
            <div className="mb-5">
              <BrandWordmark className="h-12 sm:h-14 md:h-16 lg:h-[68px] w-auto" />
            </div>
            <p className="text-[#b8c0cc] text-sm leading-relaxed max-w-sm mb-5">
              {t.footer.description}
            </p>
            <div className="flex items-center gap-2.5" data-testid="footer-socials">
              {socials.map(({ id, Icon, href }) => (
                <a
                  key={id}
                  href={href}
                  aria-label={id}
                  data-testid={`footer-social-${id}`}
                  className="w-9 h-9 rounded-full border border-[#1dc7e9]/25 bg-white/[0.02] text-[#b8c0cc] hover:text-[#1dc7e9] hover:border-[#1dc7e9]/60 hover:bg-[#1dc7e9]/10 flex items-center justify-center transition-all duration-200"
                >
                  <Icon size={15} strokeWidth={1.7} />
                </a>
              ))}
            </div>
          </div>

          {/* Soluções */}
          <FCol title={t.footer.solutions} className="md:col-span-3 lg:col-span-2">
            {solutionNames.map((n) => (
              <FLink key={n} href="/#solucoes">{n}</FLink>
            ))}
          </FCol>

          {/* Canais */}
          <FCol title={t.footer.channels} className="md:col-span-2 lg:col-span-2">
            {channelNames.map((n) => (
              <FLink key={n} href="/#canais">{n}</FLink>
            ))}
          </FCol>

          {/* Empresa + Legal */}
          <div className="md:col-span-3 lg:col-span-3 grid grid-cols-2 gap-x-6">
            <FCol title={t.footer.company}>
              {t.footer.companyLinks.map((n) => (
                <FLink key={n} href="/#">{n}</FLink>
              ))}
            </FCol>
            <FCol title={t.footer.legal}>
              {t.footer.legalLinks.map((n) => (
                <FLink key={n} to={legalRoutes[n] || "/"}>
                  {n}
                </FLink>
              ))}
            </FCol>
          </div>
        </div>

        {/* Contact bar */}
        <div className="border-y border-white/5 py-6 grid grid-cols-1 md:grid-cols-3 gap-5 items-center mb-8">
          <a
            href="mailto:comercial@documentoexpress.io"
            className="flex items-center gap-2.5 text-[#b8c0cc] hover:text-[#1dc7e9] text-sm transition-colors"
            data-testid="footer-email"
          >
            <span className="w-9 h-9 rounded-full border border-[#1dc7e9]/25 bg-[#1dc7e9]/5 flex items-center justify-center text-[#1dc7e9]">
              <Mail size={14} />
            </span>
            comercial@documentoexpress.io
          </a>
          <a
            href="tel:+551140001234"
            className="flex items-center gap-2.5 text-[#b8c0cc] hover:text-[#1dc7e9] text-sm transition-colors"
            data-testid="footer-phone"
          >
            <span className="w-9 h-9 rounded-full border border-[#1dc7e9]/25 bg-[#1dc7e9]/5 flex items-center justify-center text-[#1dc7e9]">
              <Phone size={14} />
            </span>
            (11) 4000-1234
          </a>
          <button
            onClick={onRequestDemo}
            className="md:justify-self-end inline-flex items-center gap-2 border border-[#25D366]/60 bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 hover:border-[#25D366] font-semibold text-sm px-5 py-3 rounded-lg transition-all duration-300"
            data-testid="footer-specialist-btn"
          >
            <MessageCircle size={15} />
            {t.footer.contactCta}
          </button>
        </div>

        {/* Bottom */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[#b8c0cc]/70 text-xs">{t.footer.rights}</p>
          <div className="flex items-center gap-4">
            <span className="text-[10.5px] mono uppercase tracking-[0.2em] text-[#b8c0cc]/70">
              documentoexpress.io
            </span>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10.5px] mono uppercase tracking-[0.2em] text-[#b8c0cc]/70">
                All systems operational
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const FCol = ({ title, children, className = "" }) => (
  <div className={className}>
    <div className="text-white font-bold text-[12.5px] uppercase tracking-[0.15em] mb-4">
      {title}
    </div>
    <div className="flex flex-col gap-2">{children}</div>
  </div>
);

const FLink = ({ children, href, to }) => {
  if (to) {
    return (
      <Link
        to={to}
        className="text-[#b8c0cc] hover:text-[#1dc7e9] text-[13px] transition-colors duration-200"
      >
        {children}
      </Link>
    );
  }
  return (
    <a
      href={href || "#"}
      className="text-[#b8c0cc] hover:text-[#1dc7e9] text-[13px] transition-colors duration-200"
    >
      {children}
    </a>
  );
};

export default Footer;
